// POS ↔ API V2: chuyển dữ liệu API sang kiểu của màn POS, và điều phối các bước thanh toán (checkout → thu thêm từng dòng)
import { useEffect, useState } from 'react';
import { mixGetV1 } from 'services/mixin';
import {
  checkoutPosAppointment, createPosWalkInOrder, getPosOrder, payPosOrder, updatePosAppointmentServices,
  type PosAppointmentCart, type PosCartItem, type PosInstallmentInput, type PosOrderDetail, type PosPaymentMethod, type PosResult, type PosWaitingOrder,
  type PosWalkInPayment,
} from 'api/PosV2';
import type {
  AppointmentSnapshot, CartLine, CatalogItem, Customer, Order, PaymentLineInput, PaymentMethod, PosRole, PricedLine, Transaction, WaitingAppointment,
} from './data';
import type { PricingResult } from './pricing';

/**
 * Hạn mức duyệt chiết khấu của server (V2.1), % tính trên tổng tiền gốc toàn đơn:
 * ≤ 10% thu ngân tự áp dụng · > 10% đến ≤ 30% cần Quản lý duyệt · > 30% cần Admin / Ban Giám Đốc duyệt.
 */
export const SERVER_DISCOUNT_LIMIT = 10;
export const MANAGER_DISCOUNT_LIMIT = 30;
export type ApproverRole = 'manager' | 'admin';
export const APPROVER_LABEL: Record<ApproverRole, string> = { manager: 'Quản lý', admin: 'Admin / Ban Giám Đốc' };

/** Vai trò tối thiểu phải duyệt mức chiết khấu này; null nếu thu ngân tự áp dụng được */
export const requiredApprover = (percent: number): ApproverRole | null =>
  percent > MANAGER_DISCOUNT_LIMIT + 1e-9 ? 'admin' : percent > SERVER_DISCOUNT_LIMIT + 1e-9 ? 'manager' : null;

/** Vai trò POS của người đăng nhập có đủ thẩm quyền duyệt mức đó không */
export const canApprove = (role: PosRole, need: ApproverRole) => role === 'admin' || (role === 'manager' && need === 'manager');

/** Phương thức thanh toán qua API; ví trả trước chỉ khi biết số dư ví của khách */
export const remoteMethods = (walletKnown: boolean): PaymentMethod[] =>
  walletKnown ? ['cash', 'transfer', 'card', 'ewallet', 'prepaid'] : ['cash', 'transfer', 'card', 'ewallet'];

const TO_API: Record<PaymentMethod, PosPaymentMethod> = {
  cash: 'CASH',
  transfer: 'BANK_TRANSFER',
  card: 'POS_CARD',
  ewallet: 'EWALLET',
  prepaid: 'PREPAID',
};
// WALLET: tên cũ trước V2.1 (đơn tạo trước đó) = ví điện tử
const FROM_API: Record<string, PaymentMethod> = { CASH: 'cash', BANK_TRANSFER: 'transfer', POS_CARD: 'card', EWALLET: 'ewallet', PREPAID: 'prepaid', WALLET: 'ewallet' };
export const methodFromApi = (m: string): PaymentMethod | null => FROM_API[m] || null;

/** Thông báo lỗi API kèm gợi ý xử lý theo mã lỗi nghiệp vụ */
export const explainPosError = (res: PosResult<unknown>, fallback: string) => {
  const msg = res.message || fallback;
  switch (res.code) {
    case 'INVALID_FINAL_AMOUNT':
      return `${msg} Đóng lịch hẹn và nạp lại giỏ hàng rồi thử lại.`;
    case 'DISCOUNT_APPROVAL_REQUIRED':
      return `${msg}${res.error?.require_role === 'ADMIN' ? ' (cần Admin / Ban Giám Đốc)' : ''}`;
    case 'INSTALLMENT_FIRST_TOO_LOW':
      return `${msg} Tăng đợt 1 lên tối thiểu ${res.error?.min_first_percent ?? 30}% hoặc đăng nhập tài khoản Quản lý để duyệt.`;
    case 'INSTALLMENT_PLAN_INVALID':
      return `${msg} Kiểm tra lại số tiền và ngày hạn các đợt.`;
    case 'INSTALLMENT_PLAN_MISMATCH':
      return `${msg} Đơn đang trả góp – đổi tổng tiền phải lập lại kế hoạch đợt.`;
    case 'APPOINTMENT_ALREADY_CHECKED_OUT':
      return `${msg}${res.error?.data?.order_code ? ` (đơn ${res.error.data.order_code})` : ''} Mở Tra đơn / Thu nợ để thu tiếp.`;
    case 'APPOINTMENT_NOT_READY':
      return `${msg} Lịch hẹn phải ở S7 – Xác nhận làm và đã check-in.`;
    case 'PAYMENT_EXCEEDS_DEBT':
      return `${msg} Khách chỉ còn nợ ${new Intl.NumberFormat('vi-VN').format(num(res.error?.debt_amount))}đ.`;
    case 'GUARANTOR_NOT_AUTHORIZED':
      return `${msg} Người bảo lãnh phải là Quản lý.`;
    case 'FULL_PAYMENT_REQUIRED':
      return `${msg} Bán lẻ phải thu đủ tổng đơn.`;
    case 'PRODUCT_INACTIVE':
    case 'PRODUCT_NOT_FOUND':
      return `${msg} Xóa sản phẩm đó khỏi giỏ rồi thử lại.`;
    case 'PAYMENT_EXCEEDS_TOTAL':
      return `${msg} Chuyển khoản / thẻ / ví không được vượt tổng đơn – phần dư chỉ nhận bằng tiền mặt.`;
    default:
      return msg;
  }
};

/** "HH:mm DD/MM/YYYY" hoặc "HH:mm:ss DD/MM/YYYY" → ISO; chuỗi ISO giữ nguyên; không đọc được → '' */
export const parseViDateTime = (s?: string | null): string => {
  if (!s) return '';
  const m = s.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const [, hh, mm, ss, dd, mo, yyyy] = m;
    const d = new Date(Number(yyyy), Number(mo) - 1, Number(dd), Number(hh), Number(mm), Number(ss || 0));
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
};

const num = (v: unknown) => Number(v ?? 0) || 0;
const digits = (v: unknown) => String(v ?? '').replace(/\D/g, '');

/** API tự nối "(+N mục khác)" vào tên dịch vụ chính — bỏ đi vì màn hình đã hiện số dịch vụ */
export const primaryServiceName = (s?: string | null) => (s || '').replace(/\s*\(\+\d+\s*mục khác\)\s*$/i, '');
const aptPath = (id: number | string) => `/execution-schedule/detail/${id}`;

export const toWaiting = (w: PosWaitingOrder): WaitingAppointment => ({
  id: String(w.appointment_id),
  code: w.appointment_code || `#${w.appointment_id}`,
  customerName: w.customer_name || '',
  customerPhone: digits(w.phone),
  branchId: w.branch_id ?? null,
  branchName: w.branch_name || '',
  tagName: w.status_label || w.tag_name || '',
  consultant: w.consultant_name || '',
  scheduledAt: parseViDateTime(w.scheduled_at_iso || w.scheduled_at),
  waitingSince: parseViDateTime(w.waiting_since_iso || w.waiting_since),
  primaryService: primaryServiceName(w.primary_service),
  itemsCount: num(w.items_count),
  total: num(w.total_amount),
  ...(Array.isArray(w.item_types) ? { itemTypes: w.item_types } : {}),
});

/** Giảm của API tính trên MỖI đơn vị → tổng giảm cả dòng */
const lineDiscountTotal = (it: PosCartItem) => num(it.discount) * Math.max(1, num(it.quantity));

/** Giỏ lịch hẹn (API 2) → bản chụp lịch hẹn + các dòng giỏ hàng */
export const cartFromApi = (c: PosAppointmentCart): { snapshot: AppointmentSnapshot; lines: CartLine[] } => {
  const items = c.items || [];
  const aptLines = items.map(it => ({
    serviceId: String(it.service_id ?? it.id),
    name: it.product_name || '',
    price: num(it.price),
    qty: Math.max(1, num(it.quantity)),
    discount: lineDiscountTotal(it),
    code: it.product_code || '',
    staffId: it.staff_id ? String(it.staff_id) : '',
    staffName: it.staff_name || '',
  }));
  const snapshot: AppointmentSnapshot = {
    id: String(c.appointment_id),
    code: c.appointment_code || `#${c.appointment_id}`,
    path: aptPath(c.appointment_id),
    consultant: c.consultant_name || '',
    lines: aptLines,
    total: num(c.final_amount),
    remote: true,
    customer: c.customer
      ? {
        id: c.customer.id, name: c.customer.name || '', phone: digits(c.customer.phone), rank: c.customer.rank || '',
        debt: num(c.customer.current_debt), wallet: walletOf(c.customer),
      }
      : null,
    branch: c.branch || null,
  };
  const stamp = Date.now();
  const lines: CartLine[] = aptLines.map((l, i) => ({
    key: `apt-${l.serviceId}-${stamp}-${i}`,
    // Không khớp danh mục POS mẫu để không áp khuyến mãi mẫu lên đơn thật
    itemId: `apt-${l.serviceId}`,
    code: l.code || 'LỊCH HẸN',
    name: l.name,
    type: 'service',
    listPrice: l.price,
    qty: l.qty,
    // Cùng mặc định % như bán lẻ; lịch hẹn đã có giảm (API trả số tiền) thì giữ đ
    discountType: l.discount > 0 ? 'đ' : '%',
    discountValue: l.discount,
    staffId: l.staffId,
    staffName: l.staffName,
    note: '',
    aptServiceId: l.serviceId,
  }));
  return { snapshot, lines };
};

/** Số dư ví trả trước; null khi API không trả trường này */
function walletOf(c: { wallet_balance?: number | null } | null | undefined): number | null {
  return c && c.wallet_balance !== undefined && c.wallet_balance !== null ? num(c.wallet_balance) : null;
}

/** Khách từ API dưới dạng Customer của POS (dùng cho popup thanh toán) */
export const customerFromApi = (
  c: { id: number; name: string; phone?: string | null; wallet_balance?: number | null; wallet?: number | null } | null | undefined,
): Customer | null =>
  c ? { id: `api-${c.id}`, name: c.name || '', phone: digits(c.phone), tier: 'Thường', prepaid: num(c.wallet ?? c.wallet_balance) } : null;

/** Staff ID của người đang đăng nhập (dùng làm approved_by) */
export const currentStaffId = (): number | null => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const id = Number(u?.info?.staff_id ?? u?.staff_id);
    return id > 0 ? id : null;
  } catch {
    return null;
  }
};

/**
 * Tách tiền thực thu từng dòng: phần tiền mặt nhập vượt là tiền thối, không ghi vào thanh toán (BR2.1).
 * Trả về các dòng có số tiền > 0 theo đúng thứ tự.
 */
export const netPayments = (payments: PaymentLineInput[], due: number) => {
  let excess = Math.max(0, payments.reduce((s, p) => s + p.amount, 0) - due);
  let change = 0;
  const lines: { method: PaymentMethod; amount: number; txnRef: string }[] = [];
  payments.forEach(p => {
    let amount = p.amount;
    if (p.method === 'cash' && excess > 0) {
      const cut = Math.min(excess, amount);
      amount -= cut;
      excess -= cut;
    }
    if (p.method === 'cash') change += Math.max(0, p.tendered - amount);
    if (amount > 0) lines.push({ method: p.method, amount, txnRef: p.txnRef.trim().toUpperCase() });
  });
  return { lines, change };
};

/**
 * (3) Lưu dịch vụ / SL / KTV / CK dòng / ghi chú lên lịch hẹn – dùng cho nút "Cập nhật" và bước đầu của thanh toán.
 * Giảm gửi lên tính trên MỖI đơn vị (gồm KM + CK dòng). Trả về giỏ mới từ hệ thống để đồng bộ màn hình.
 */
export async function saveAppointmentServicesRemote(
  appointmentId: string, cart: CartLine[], pricing: PricingResult, note: string,
): Promise<{ ok: true; cart: PosAppointmentCart | null } | { ok: false; error: string }> {
  const items = cart.map(l => {
    const priced = pricing.lines.find(p => p.key === l.key);
    const qty = Math.max(1, l.qty);
    const lineOff = (priced?.promoAmount || 0) + (priced?.lineDiscountAmount || 0);
    return {
      service_id: Number(l.aptServiceId),
      quantity: qty,
      price: l.listPrice,
      discount: Math.round(lineOff / qty),
      staff_id: l.staffId ? Number(l.staffId) : null,
    };
  });
  if (items.some(i => !i.service_id)) return { ok: false, error: 'Giỏ có dòng không thuộc lịch hẹn – không thể lưu lên hệ thống' };
  const saved = await updatePosAppointmentServices(appointmentId, { items, note: note || undefined });
  if (!saved.ok) return { ok: false, error: `Không lưu được dịch vụ lịch hẹn: ${explainPosError(saved, 'lỗi máy chủ')}` };
  return { ok: true, cart: saved.data };
}

export interface RemotePayOutcome {
  ok: boolean;
  /** Lỗi chặn ngay từ đầu (chưa tạo đơn) */
  error?: string;
  orderId?: number;
  orderCode?: string;
  change: number;
  /** Đơn đã tạo nhưng một số lượt thu sau bị lỗi */
  partialError?: string;
  detail?: PosOrderDetail | null;
}

/**
 * Thu tiền lịch hẹn: (3) lưu dịch vụ/KTV/CK → (4) checkout với dòng thanh toán đầu → (4b) thu các dòng còn lại → (2b) nạp đơn.
 * API chỉ nhận 1 phương thức mỗi lượt nên thanh toán tách dòng = nhiều lượt; lượt sau lỗi thì đơn vẫn đã tạo và còn nợ.
 */
export async function checkoutAppointmentRemote(input: {
  appointmentId: string;
  cart: CartLine[];
  pricing: PricingResult;
  payments: PaymentLineInput[];
  cashier: string;
  note: string;
  approvedBy?: number | null;
  approvedNote?: string;
  /** Trả góp: kế hoạch đợt (đợt 1 = hôm nay) – server lưu và theo dõi từng đợt */
  installments?: PosInstallmentInput[];
  /** ID Quản lý duyệt khi đợt 1 < 30% tổng đơn */
  planApprovedBy?: number | null;
}): Promise<RemotePayOutcome> {
  const { appointmentId, cart, pricing, cashier, note, installments } = input;
  const totals = pricing.totals;
  const isPlan = !!installments?.length;

  const net = netPayments(input.payments, totals.total);
  const { change } = net;
  // Trả góp: lượt checkout (1 phương thức) phải ≥ đợt 1 → đưa dòng lớn nhất lên đầu, kiểm tra trước khi ghi gì lên hệ thống
  const lines = isPlan ? [...net.lines].sort((a, b) => b.amount - a.amount) : net.lines;
  if (!lines.length) return { ok: false, change: 0, error: 'Chưa có số tiền thanh toán' };
  if (isPlan && lines[0].amount < installments![0].amount) {
    return {
      ok: false, change: 0,
      error: `Trả góp: phải thu đợt 1 (${new Intl.NumberFormat('vi-VN').format(installments![0].amount)}đ) bằng một phương thức – không tách đợt 1 thành nhiều dòng thanh toán.`,
    };
  }

  const saved = await saveAppointmentServicesRemote(appointmentId, cart, pricing, note);
  if (!saved.ok) return { ok: false, change: 0, error: saved.error };

  let paid = lines[0].amount;
  const first = lines[0];
  const checkout = await checkoutPosAppointment(appointmentId, {
    amount: first.amount,
    payment_method: TO_API[first.method],
    // Trả góp: is_deposit = false (server tự tính nợ theo các đợt)
    is_deposit: isPlan ? false : paid < totals.total,
    ...(isPlan ? { installments, ...(input.planApprovedBy ? { plan_approved_by: input.planApprovedBy } : {}) } : {}),
    order_discount: totals.orderPromo + totals.orderDiscount,
    voucher_discount: totals.voucher,
    rounding_amount: totals.rounding,
    final_amount: totals.total,
    ...(input.approvedBy ? { approved_by: input.approvedBy, approved_note: input.approvedNote } : {}),
    ...(first.txnRef ? { transaction_code: first.txnRef } : {}),
    cashier_name: cashier,
    ...(note ? { note } : {}),
  });
  if (!checkout.ok || !checkout.data) return { ok: false, change: 0, error: explainPosError(checkout, 'Thanh toán thất bại') };

  const { order_id: orderId, order_code: orderCode } = checkout.data;
  const finalAmount = num(checkout.data.final_amount) || totals.total;
  let partialError: string | undefined;
  for (const l of lines.slice(1)) {
    paid += l.amount;
    const res = await payPosOrder(orderId, {
      paid_amount: l.amount,
      payment_method: TO_API[l.method],
      is_deposit: isPlan ? false : paid < finalAmount,
      ...(l.txnRef ? { transaction_code: l.txnRef } : {}),
      cashier_name: cashier,
    });
    if (!res.ok) {
      partialError = `Đã tạo đơn ${orderCode} nhưng chưa ghi được khoản ${new Intl.NumberFormat('vi-VN').format(l.amount)}đ: ${explainPosError(res, 'lỗi máy chủ')}. Thu lại phần còn nợ ở mục Tra đơn / Thu nợ.`;
      break;
    }
  }

  const detail = await getPosOrder(orderId);
  return { ok: true, orderId, orderCode, change, partialError, detail: detail.data };
}

/**
 * Thu nợ / thanh toán thêm cho đơn đã tạo (4b), mỗi dòng thanh toán là một lượt.
 * Đơn trả góp: server tự phân bổ khoản thu vào các đợt theo thứ tự; is_deposit = false.
 */
export async function collectOrderRemote(orderId: number, debt: number, payments: PaymentLineInput[], cashier: string, isInstallment = false) {
  const { lines, change } = netPayments(payments, debt);
  let left = debt;
  let done = 0;
  for (const l of lines) {
    left -= l.amount;
    const res = await payPosOrder(orderId, {
      paid_amount: l.amount,
      payment_method: TO_API[l.method],
      is_deposit: isInstallment ? false : left > 0,
      ...(l.txnRef ? { transaction_code: l.txnRef } : {}),
      cashier_name: cashier,
    });
    if (!res.ok) {
      const why = explainPosError(res, 'Thu nợ thất bại');
      return {
        ok: done > 0, change: done > 0 ? change : 0,
        error: done > 0 ? `Đã ghi ${done}/${lines.length} khoản; khoản tiếp theo lỗi: ${why}` : why,
      };
    }
    done += 1;
  }
  return { ok: true, change, error: undefined as string | undefined };
}

/* ---------------------------------------------------------------- bán lẻ (API 7) */

/** Sản phẩm hệ thống trong giỏ POS: itemId = "sp-<id sản phẩm>" */
const PRODUCT_PREFIX = 'sp-';
export const productIdOf = (itemId: string) => (itemId.startsWith(PRODUCT_PREFIX) ? Number(itemId.slice(PRODUCT_PREFIX.length)) || 0 : 0);

const apiList = (res: any): any[] => {
  const d = res?.data;
  return Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : Array.isArray(d?.data) ? d.data : [];
};

/** Sản phẩm đang kinh doanh → mặt hàng bán lẻ. API chưa có tồn kho / danh mục nên stock = null, group rỗng */
const productToCatalog = (p: any): CatalogItem => {
  const price = num(p.price);
  const sale = num(p.discount_price);
  return {
    id: `${PRODUCT_PREFIX}${p.id}`,
    code: p.code || '',
    barcode: p.code_2 || '',
    name: p.name || `SP #${p.id}`,
    // Giá bán: giá khuyến mãi nếu có (server cũng dùng discount_price khi không gửi price)
    price: sale > 0 && sale < price ? sale : price,
    type: 'product',
    group: p.unit || '',
    stock: null,
    ...(sale > 0 && sale < price ? { badge: `-${Math.round((1 - sale / price) * 100)}%` } : {}),
  };
};

/** Danh mục bán lẻ từ hệ thống (một lần mỗi phiên, tải lại bằng reload) */
let productCache: CatalogItem[] | null = null;
export function useRemoteProducts() {
  const [items, setItems] = useState<CatalogItem[]>(productCache || []);
  const [loading, setLoading] = useState(!productCache);
  const [error, setError] = useState('');
  const load = async () => {
    setLoading(true);
    try {
      const res = await mixGetV1('/api/v1/be/products', { all: 1, status: 0 });
      const list = apiList(res).filter(p => p?.id && num(p.status) === 0).map(productToCatalog);
      productCache = list;
      setItems(list);
      setError('');
    } catch {
      setError('Không tải được danh sách sản phẩm');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (!productCache) load(); }, []);
  return { items, loading, error, reload: load };
}

export interface ServerBranch { id: number; name: string }

/** Chi nhánh trên hệ thống – nơi ghi nhận đơn bán lẻ */
let branchCache: ServerBranch[] | null = null;
export function useServerBranches() {
  const [items, setItems] = useState<ServerBranch[]>(branchCache || []);
  useEffect(() => {
    if (branchCache) return;
    let alive = true;
    mixGetV1('/api/v1/be/branches', { all: 1 })
      .then((res: any) => {
        const list = apiList(res).filter(b => b?.id && num(b.status) === 0).map(b => ({ id: Number(b.id), name: String(b.name || `CN #${b.id}`) }));
        branchCache = list;
        if (alive) setItems(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return items;
}

/** Tìm khách hàng trên hệ thống theo tên / SĐT / mã KH */
export async function searchCustomersRemote(q: string): Promise<Customer[]> {
  const res = await mixGetV1('/api/v1/be/customers', { search: encodeURIComponent(q) });
  return apiList(res).filter(c => c?.id).slice(0, 8).map(c => ({
    id: `api-${c.id}`,
    name: c.fullname || c.display_name || `KH #${c.id}`,
    phone: digits(c.phone || c.phones?.[0]),
    tier: 'Thường' as const,
    prepaid: 0,
    source: c.customer_code || undefined,
  }));
}

const customerIdOf = (c: Customer | null) => (c && c.id.startsWith('api-') ? Number(c.id.slice(4)) || 0 : 0);

/** Mã chống tạo trùng đơn bán lẻ (gửi lại cùng mã → server trả đơn cũ) */
export const newRequestId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
      const r = (Math.random() * 16) | 0;
      return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

/**
 * Bán lẻ: tạo đơn khách lẻ và thu tiền trong một lượt (API 7), rồi nạp chi tiết đơn (2b).
 * Bán lẻ thu đủ một lần; tiền mặt nhập vượt là tiền thối (gửi `tendered`, server tính lại).
 */
export async function createWalkInRemote(input: {
  requestId: string;
  cart: CartLine[];
  pricing: PricingResult;
  payments: PaymentLineInput[];
  customer: Customer | null;
  branchId: number | null;
  cashier: string;
  note: string;
  approvedBy?: number | null;
  approvedNote?: string;
}): Promise<RemotePayOutcome> {
  const { cart, pricing, customer } = input;
  const totals = pricing.totals;
  const missing = cart.find(l => !productIdOf(l.itemId));
  if (missing) return { ok: false, change: 0, error: `“${missing.name}” không có trên hệ thống – xóa khỏi giỏ và chọn lại từ danh mục.` };

  // Tách tiền thực thu từng dòng như thu tiền lịch hẹn, giữ tiền khách đưa của dòng tiền mặt
  let excess = Math.max(0, input.payments.reduce((s, p) => s + p.amount, 0) - totals.total);
  let change = 0;
  const payments: PosWalkInPayment[] = [];
  input.payments.forEach(p => {
    let amount = p.amount;
    if (p.method === 'cash' && excess > 0) {
      const cut = Math.min(excess, amount);
      amount -= cut;
      excess -= cut;
    }
    if (amount <= 0 || p.method === 'prepaid') return;
    if (p.method === 'cash') change += Math.max(0, p.tendered - amount);
    const ref = p.txnRef.trim().toUpperCase();
    payments.push({
      method: TO_API[p.method] as PosWalkInPayment['method'],
      amount,
      ...(p.method === 'cash' ? { tendered: Math.max(amount, p.tendered) } : {}),
      ...(ref ? { transaction_code: ref } : {}),
    });
  });
  if (!payments.length) return { ok: false, change: 0, error: 'Chưa có số tiền thanh toán' };

  const res = await createPosWalkInOrder({
    client_request_id: input.requestId,
    ...(customerIdOf(customer) ? { customer_id: customerIdOf(customer) } : {}),
    ...(customer ? { customer_name: customer.name, ...(customer.phone ? { customer_phone: customer.phone } : {}) } : {}),
    ...(input.branchId ? { branch_id: input.branchId } : {}),
    items: cart.map(l => {
      const priced = pricing.lines.find(p => p.key === l.key);
      const qty = Math.max(1, l.qty);
      const lineOff = (priced?.promoAmount || 0) + (priced?.lineDiscountAmount || 0);
      return {
        item_type: 'product' as const,
        product_id: productIdOf(l.itemId),
        product_code: l.code || undefined,
        product_name: l.name,
        quantity: qty,
        price: l.listPrice,
        // Giảm gửi lên tính trên MỖI đơn vị
        discount: Math.round(lineOff / qty),
        staff_id: l.staffId ? Number(l.staffId) : null,
      };
    }),
    payments,
    order_discount: totals.orderPromo + totals.orderDiscount,
    rounding_amount: totals.rounding,
    final_amount: totals.total,
    ...(input.approvedBy ? { approved_by: input.approvedBy, approved_note: input.approvedNote } : {}),
    cashier_name: input.cashier,
    ...(input.note ? { note: input.note } : {}),
  });
  if (!res.ok || !res.data) return { ok: false, change: 0, error: explainPosError(res, 'Thanh toán thất bại') };

  const { order_id: orderId, order_code: orderCode } = res.data;
  const detail = await getPosOrder(orderId);
  return {
    ok: true, orderId, orderCode,
    change: res.data.change_amount ?? change,
    ...(res.data.duplicate ? { partialError: `Đơn ${orderCode} đã được tạo ở lần gửi trước – không tạo thêm đơn mới.` } : {}),
    detail: detail.data,
  };
}

/** Đơn API (2b) → Order của POS để hiển thị và in hóa đơn */
export const orderFromApi = (d: PosOrderDetail): { order: Order; transactions: Transaction[] } => {
  const firstPay = d.payments?.[0];
  const createdAt = parseViDateTime(d.order_date_iso || d.created_at_iso || firstPay?.created_at_iso || firstPay?.created_at) || new Date().toISOString();
  const lines: PricedLine[] = (d.items || []).map((it, i) => {
    const qty = Math.max(1, num(it.quantity));
    const gross = num(it.price) * qty;
    const off = lineDiscountTotal(it);
    const afterLine = num(it.sub_total) || gross - off;
    return {
      key: `api-${it.id ?? i}`, itemId: `api-${it.id ?? i}`, code: it.product_code || '', name: it.product_name || '', type: 'service',
      listPrice: num(it.price), qty, discountType: 'đ', discountValue: off, staffId: it.staff_id ? String(it.staff_id) : '',
      staffName: it.staff_name || '', note: '',
      gross, promoAmount: 0, lineDiscountAmount: off, afterLine, allocated: 0, net: afterLine, refundedQty: 0, refundedAmount: 0,
    };
  });
  const lineDiscount = lines.reduce((s, l) => s + l.lineDiscountAmount, 0);
  const transactions: Transaction[] = (d.payments || []).map(p => ({
    id: `PAY-${p.id}`, orderId: d.order_code, branchId: '', at: parseViDateTime(p.created_at_iso || p.created_at) || createdAt,
    kind: 'payment', method: FROM_API[p.payment_method] || 'cash', amount: num(p.amount),
    txnRef: p.transaction_code || '', cashier: p.cashier_name || '',
  }));
  const order: Order = {
    id: d.order_code, branchId: '', createdAt, cashier: firstPay?.cashier_name || '',
    customerId: d.customer ? String(d.customer.id) : null, customerName: d.customer?.name || 'Khách lẻ', customerPhone: digits(d.customer?.phone),
    lines,
    totals: {
      subtotal: num(d.subtotal), itemPromo: 0, lineDiscount, orderPromo: 0, orderDiscount: num(d.order_discount),
      voucher: num(d.voucher_discount), rounding: num(d.rounding_amount), total: num(d.final_amount),
    },
    orderDiscountInput: { type: 'đ', value: num(d.order_discount) }, voucherCode: d.voucher_code || '',
    approvedBy: d.approver_name || undefined, note: d.note || '',
    paid: num(d.paid_amount), refunded: 0, debt: num(d.debt_amount), status: num(d.debt_amount) > 0 ? 'debt' : 'completed',
    source: 'pos', printCount: 0, logs: [],
  };
  return { order, transactions };
};

export const POS_STATUS_LABEL: Record<string, string> = {
  WAITING_PAYMENT: 'Chờ thanh toán',
  COMPLETED_PAYMENT: 'Đã thu – chờ vào phòng',
  IN_PROGRESS: 'Đang làm dịch vụ',
};
export const PAYMENT_STATUS_LABEL: Record<string, string> = {
  UNPAID: 'Chưa thanh toán',
  PARTIALLY_PAID: 'Còn nợ',
  PAID: 'Đã thanh toán đủ',
};

export interface StaffOption {
  id: string;
  name: string;
  /** Phòng ban, không có thì chức danh – hiện mờ cạnh tên */
  dept: string;
  /** Thuộc phòng KTV / chức danh kỹ thuật – xếp nhóm riêng trong dropdown */
  isKtv: boolean;
}

/** API staffs chưa có chi nhánh (branch_id rỗng) nên chỉ phân nhóm theo phòng ban / chức danh */
const KTV_PATTERN = /\bktv\b|ky thuat|kỹ thuật/i;

/** Danh sách nhân viên thật cho dropdown KTV (một lần cho cả phiên) */
let staffCache: StaffOption[] | null = null;
export function useStaffOptions() {
  const [items, setItems] = useState<StaffOption[]>(staffCache || []);
  useEffect(() => {
    if (staffCache) return;
    let alive = true;
    mixGetV1('/api/v1/be/staffs', { limit: 500 })
      .then((res: any) => {
        const list: StaffOption[] = (res?.data?.items || [])
          .filter((s: any) => s?.id)
          .map((s: any): StaffOption => {
            const dept = String(s.department?.name || s.position || '').trim();
            const isKtv = [s.department?.name, s.position, s.role?.name].some(v => v && KTV_PATTERN.test(String(v)));
            return { id: String(s.id), name: s.name || s.fullname || `NV #${s.id}`, dept, isKtv };
          })
          .sort((a: StaffOption, b: StaffOption) => a.name.localeCompare(b.name, 'vi'));
        staffCache = list;
        if (alive) setItems(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return items;
}
